import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { aboutInfo } from '@/mocks/about';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      name: '91影视',
      url: 'https://www.91yingshi.com',
      description: '海量高清影视资源免费在线观看平台',
      foundingDate: '2018',
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'contact@91yingshi.com',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
    },
    {
      '@type': 'WebPage',
      name: '关于91影视 - 品牌介绍与联系方式',
      description: '了解91影视的品牌故事、用户口碑评价以及联系方式。91影视成立于2018年，是国内领先的在线影视资源平台。',
      url: 'https://www.91yingshi.com/about',
      isPartOf: { '@type': 'WebSite', name: '91影视', url: 'https://www.91yingshi.com' },
    },
    {
      '@type': 'FAQPage',
      mainEntity: aboutInfo.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: { '@type': 'Answer', text: faq.answer },
      })),
    },
  ],
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91yingshi.com' },
    { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.91yingshi.com/about' },
  ],
};

export default function AboutPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormSubmitted(true);
      return;
    }
    setIsSubmitting(true);
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9snn6d8o1vn02ekuikg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      if (
        response.ok &&
        parsed &&
        parsed.code === 'OK'
      ) {
        setFormSubmitted(true);
        setFormName('');
        setFormEmail('');
        setFormMessage('');
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam')) {
          setFormSubmitted(true);
        } else {
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解91影视的品牌故事、用户口碑评价以及联系方式。91影视成立于2018年，是国内领先的在线影视资源平台，提供海量高清影视资源免费在线观看。"
        keywords="91影视关于我们,91影视联系方式,91影视品牌介绍,91影视口碑"
        canonicalUrl="https://www.91yingshi.com/about"
        schema={{ ...aboutSchema, ...breadcrumbSchema } as Record<string, unknown>}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <section className="bg-background-100/50 border-b border-background-200/50">
            <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-10 md:py-16">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
                <Link to="/" className="hover:text-foreground-200 transition-colors cursor-pointer">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-300">关于我们</span>
              </nav>

              <div className="text-center max-w-3xl mx-auto">
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-50 font-heading mb-4">
                  关于<span className="text-primary-400">91影视</span>
                </h1>
                <p className="text-sm md:text-base text-foreground-400 leading-relaxed">
                  {aboutInfo.brand.slogan}
                </p>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8">
            <div className="max-w-[1440px] mx-auto">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-6">
                  品牌故事
                </h2>
                <div className="text-sm text-foreground-400 leading-relaxed space-y-4">
                  {aboutInfo.brand.description.split('\n\n').map((para, i) => (
                    <p key={i}>{para.trim()}</p>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100/50">
            <div className="max-w-[1440px] mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-3">
                  核心优势
                </h2>
                <p className="text-sm text-foreground-500">为什么选择91影视</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {aboutInfo.brand.values.map((value) => (
                  <div
                    key={value.title}
                    className="bg-background-100 rounded-xl p-6 border border-background-200/50 text-center"
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center mx-auto mb-4">
                      <i className={`${value.icon} text-xl text-primary-400`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-100 mb-2">
                      {value.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8">
            <div className="max-w-[1440px] mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-3">
                  {aboutInfo.reputation.title}
                </h2>
                <p className="text-sm text-foreground-500">{aboutInfo.reputation.subtitle}</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-12">
                {[
                  { value: aboutInfo.reputation.stats.totalReviews, label: '累计评价' },
                  { value: aboutInfo.reputation.stats.avgRating, label: '平均评分' },
                  { value: aboutInfo.reputation.stats.satisfaction, label: '用户满意度' },
                  { value: aboutInfo.reputation.stats.repeatUsers, label: '回头用户' },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="bg-background-100 rounded-xl p-5 text-center border border-background-200/50"
                  >
                    <div className="text-2xl md:text-3xl font-bold text-accent-400 font-heading mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {aboutInfo.reputation.reviews.map((review) => (
                  <div
                    key={review.name}
                    className="bg-background-100 rounded-xl p-5 border border-background-200/50"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={review.avatar}
                        alt={review.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground-100">
                          {review.name}
                        </div>
                        <div className="text-xs text-foreground-500">{review.date}</div>
                      </div>
                      <div className="ml-auto flex items-center gap-0.5">
                        {Array.from({ length: review.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-accent-400 text-xs"></i>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-foreground-400 leading-relaxed">{review.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100/50">
            <div className="max-w-[1440px] mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-3">
                  {aboutInfo.contact.title}
                </h2>
                <p className="text-sm text-foreground-500">{aboutInfo.contact.subtitle}</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-lg text-primary-400"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-100">电子邮箱</div>
                      <div className="text-sm text-foreground-400">{aboutInfo.contact.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-wechat-line text-lg text-accent-400"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-100">微信公众号</div>
                      <div className="text-sm text-foreground-400">{aboutInfo.contact.wechat}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-weibo-line text-lg text-primary-400"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-100">官方微博</div>
                      <div className="text-sm text-foreground-400">{aboutInfo.contact.weibo}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-lg text-secondary-400"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-100">工作时间</div>
                      <div className="text-sm text-foreground-400">{aboutInfo.contact.workingHours}</div>
                      <div className="text-xs text-foreground-600 mt-0.5">{aboutInfo.contact.responseTime}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-background-100 rounded-xl p-6 border border-background-200/50">
                  <h3 className="text-base font-semibold text-foreground-100 mb-4">发送消息</h3>

                  {formSubmitted ? (
                    <div className="text-center py-10">
                      <div className="w-14 h-14 rounded-full bg-primary-500/10 flex items-center justify-center mx-auto mb-3">
                        <i className="ri-check-line text-2xl text-primary-400"></i>
                      </div>
                      <p className="text-sm font-medium text-foreground-100 mb-1">发送成功！</p>
                      <p className="text-xs text-foreground-500">我们会在24小时内回复您的消息</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                      <div>
                        <label className="block text-xs font-medium text-foreground-400 mb-1.5">
                          姓名 <span className="text-primary-400">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formName}
                          onChange={(e) => setFormName(e.target.value)}
                          className="w-full px-3 py-2.5 rounded-lg bg-background-200 border border-background-300/50 text-sm text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-400 mb-1.5">
                          邮箱 <span className="text-primary-400">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formEmail}
                          onChange={(e) => setFormEmail(e.target.value)}
                          className="w-full px-3 py-2.5 rounded-lg bg-background-200 border border-background-300/50 text-sm text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-400 mb-1.5">
                          留言内容 <span className="text-primary-400">*</span>
                        </label>
                        <textarea
                          name="message"
                          required
                          maxLength={500}
                          value={formMessage}
                          onChange={(e) => setFormMessage(e.target.value)}
                          rows={4}
                          className="w-full px-3 py-2.5 rounded-lg bg-background-200 border border-background-300/50 text-sm text-foreground-200 placeholder-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                          placeholder="请输入您的留言内容（不超过500字）"
                        />
                        <p className="text-xs text-foreground-600 mt-1">
                          {formMessage.length}/500
                        </p>
                      </div>
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />
                      {formError && (
                        <p className="text-xs text-red-400">{formError}</p>
                      )}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full px-6 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
                      >
                        {isSubmitting ? '发送中...' : '发送消息'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-3">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500">关于91影视，您想了解的都在这</p>
              </div>
              <div className="space-y-3">
                {aboutInfo.faqs.map((faq, index) => (
                  <FAQItem key={index} question={faq.question} answer={faq.answer} index={index} />
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

function FAQItem({
  question,
  answer,
  index,
}: {
  question: string;
  answer: string;
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-background-100 rounded-xl border border-background-200/50 overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-sm font-medium text-foreground-100 pr-4">{question}</span>
        <i
          className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-300 flex-shrink-0 ${
            isOpen ? 'rotate-180' : ''
          }`}
        ></i>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96' : 'max-h-0'
        }`}
      >
        <p className="px-5 pb-4 text-sm text-foreground-500 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}